import {defineStore} from 'pinia'
import {computed, ref, shallowRef} from 'vue'
import {useWorkspaceStore} from '@toolify/client/src/stores/WorkspaceStore/useWorkspaceStore'
import {useMinimizedItemApiStore} from '@toolify/client/src/stores/api/MinimizedItemApiStore/useMinimizedItemApiStore'
import {useSocketListener} from '@toolify/client/src/composables/useSocketListener'
import {useMessages} from '@toolify/client/src/modules/notifications/composables/useMessages'
import {i18n} from '@toolify/client/src/setup/i18n'
import {GlobalSocketEventType} from '@toolify/server/src/services/SocketService/enum/GlobalSocketEventType'
import {ResourceType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/ResourceType'
import {mapMinimizedItemResponseInstance} from '@toolify/client/src/modules/api/singletons/mapMinimizedItemResponseInstance'
import {IToJson} from '@toolify/client/src/types/IToJson'
import {
  IMinimizedItemResponseInstance,
} from '@toolify/server/src/controllers/MinimizedItemController/types/IMinimizedItemResponseInstance'
import {
  IMinimizedItemViewState,
} from '@toolify/server/src/models/mongoose/MinimizedItemModel/types/IMinimizedItemViewState'
import {
  IMinimizedItemCreatedEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/IMinimizedItemCreatedEmitPayload'
import {
  IMinimizedItemUpdatedEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/IMinimizedItemUpdatedEmitPayload'
import {
  IMinimizedItemDeletedEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/IMinimizedItemDeletedEmitPayload'
import {
  IMinimizedItemsReorderedEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/IMinimizedItemsReorderedEmitPayload'
import {IOpenMinimizableRecord} from '@toolify/client/src/modules/minimizedItems/types/IOpenMinimizableRecord'
import {IMinimizeRecordInput} from '@toolify/client/src/modules/minimizedItems/types/IMinimizeRecordInput'
import {IMinimizeFlightRequest} from '@toolify/client/src/modules/minimizedItems/types/IMinimizeFlightRequest'

// MVP minimizes only database records; the model is generic, so documents and whiteboards can follow
const MINIMIZED_RESOURCE_TYPE = ResourceType.DatabaseItem

function sortByPosition(items: IMinimizedItemResponseInstance[]): IMinimizedItemResponseInstance[] {
  return items.slice().sort((first, second) => first.position - second.position)
}

function renumber(items: IMinimizedItemResponseInstance[]): IMinimizedItemResponseInstance[] {
  return items.map((item, index) => (item.position === index ? item : {
    ...item,
    position: index,
  }))
}

export const useMinimizedItemStore = defineStore('minimizedItemStore', () => {
  const workspaceStore = useWorkspaceStore()
  const minimizedItemApiStore = useMinimizedItemApiStore()
  const messages = useMessages()
  const t = i18n.global.t

  const minimizedItemsByWorkspaceId = ref<Map<string, IMinimizedItemResponseInstance[]>>(new Map())
  const loadedWorkspaceIds = ref<Set<string>>(new Set())
  const pendingFetchByWorkspaceId = new Map<string, Promise<void>>()

  // Record surfaces (item card, side panel) open in this tab; the last one is on top and answers Alt+M
  const openRecords = shallowRef<IOpenMinimizableRecord[]>([])
  // View state handed from the bar to the item card that is about to open
  const pendingRestoreByResourceId = new Map<string, IMinimizedItemViewState>()
  // Bar elements, used as the target of the minimize animation
  const slotElementByResourceId = new Map<string, HTMLElement>()
  let overflowElement: HTMLElement | null = null
  let barElement: HTMLElement | null = null
  const flashRequest = ref<{resourceId: string, at: number} | null>(null)
  // A window on its way into the bar; the bar draws it, because only the bar knows where the element is
  const flightRequest = shallowRef<IMinimizeFlightRequest | null>(null)

  const currentWorkspaceId = computed(() => workspaceStore.currentWorkspace?.id ?? null)

  const minimizedItems = computed<IMinimizedItemResponseInstance[]>(() => {
    if(!currentWorkspaceId.value) {
      return []
    }
    return minimizedItemsByWorkspaceId.value.get(currentWorkspaceId.value) ?? []
  })

  const minimizedResourceIds = computed(() => new Set(minimizedItems.value.map(item => item.resourceId)))

  const openResourceIds = computed(() => new Set(openRecords.value.map(openRecord => openRecord.resourceId)))

  const topOpenRecord = computed<IOpenMinimizableRecord | null>(() => {
    return openRecords.value[openRecords.value.length - 1] ?? null
  })

  function setWorkspaceItems(workspaceId: string, items: IMinimizedItemResponseInstance[]) {
    const next = new Map(minimizedItemsByWorkspaceId.value)
    next.set(workspaceId, sortByPosition(items))
    minimizedItemsByWorkspaceId.value = next
  }

  function getWorkspaceItems(workspaceId: string): IMinimizedItemResponseInstance[] {
    return minimizedItemsByWorkspaceId.value.get(workspaceId) ?? []
  }

  function hasLoaded(workspaceId: string): boolean {
    return loadedWorkspaceIds.value.has(workspaceId)
  }

  async function fetchMinimizedItems(workspaceId: string): Promise<void> {
    const pending = pendingFetchByWorkspaceId.get(workspaceId)
    if(pending) {
      return pending
    }
    const request = (async() => {
      try {
        const items = await minimizedItemApiStore.getMinimizedItems(workspaceId)
        setWorkspaceItems(workspaceId, items)
        const loaded = new Set(loadedWorkspaceIds.value)
        loaded.add(workspaceId)
        loadedWorkspaceIds.value = loaded
      } finally {
        pendingFetchByWorkspaceId.delete(workspaceId)
      }
    })()
    pendingFetchByWorkspaceId.set(workspaceId, request)
    return request
  }

  function getMinimizedItem(resourceId: string): IMinimizedItemResponseInstance | null {
    return minimizedItems.value.find(item => item.resourceId === resourceId) ?? null
  }

  function isMinimized(resourceId: string): boolean {
    return Boolean(getMinimizedItem(resourceId))
  }

  function upsertItem(item: IMinimizedItemResponseInstance) {
    const items = getWorkspaceItems(item.workspaceId)
    const index = items.findIndex(existing => existing.resourceId === item.resourceId)
    if(index === -1) {
      setWorkspaceItems(item.workspaceId, [...items, item])
      return
    }
    const next = items.slice()
    next[index] = item
    setWorkspaceItems(item.workspaceId, next)
  }

  function removeItems(workspaceId: string, resourceIds: string[]) {
    const items = getWorkspaceItems(workspaceId)
    const remaining = items.filter(item => !resourceIds.includes(item.resourceId))
    if(remaining.length === items.length) {
      return
    }
    setWorkspaceItems(workspaceId, renumber(remaining))
  }

  /**
   * Puts a record into the bar (or refreshes the state of one already there). The element appears at
   * once with the preview the caller already has, so the minimize animation has a place to fly to;
   * the server answer replaces it.
   */
  const minimizeGenerationByResourceId = new Map<string, number>()

  async function minimizeRecord(input: IMinimizeRecordInput): Promise<IMinimizedItemResponseInstance | null> {
    const workspaceId = currentWorkspaceId.value
    if(!workspaceId) {
      return null
    }
    // only the newest call for a record may apply its answer or roll back, so a slow older response cannot undo a newer one
    const generation = (minimizeGenerationByResourceId.get(input.resourceId) ?? 0) + 1
    minimizeGenerationByResourceId.set(input.resourceId, generation)
    const isLatestCall = () => minimizeGenerationByResourceId.get(input.resourceId) === generation
    const items = getWorkspaceItems(workspaceId)
    const existing = items.find(item => item.resourceId === input.resourceId) ?? null
    const now = new Date()
    if(existing) {
      upsertItem({
        ...existing,
        viewState: input.viewState,
        lastSeenAt: now,
        changeCount: 0,
        preview: input.preview ?? existing.preview,
      })
    } else {
      const position = input.position ?? items.length
      const shifted = items.map(item => (item.position >= position ? {
        ...item,
        position: item.position + 1,
      } : item))
      setWorkspaceItems(workspaceId, [...shifted, {
        id: `pending:${input.resourceId}`,
        workspaceId,
        resourceType: MINIMIZED_RESOURCE_TYPE,
        resourceId: input.resourceId,
        databaseId: input.databaseId,
        position,
        minimizedAt: now,
        lastSeenAt: now,
        viewState: input.viewState,
        isUnavailable: false,
        changeCount: 0,
        preview: input.preview,
      } as IMinimizedItemResponseInstance])
    }
    try {
      const saved = await minimizedItemApiStore.minimizeResource(
        MINIMIZED_RESOURCE_TYPE,
        input.resourceId,
        input.viewState,
        existing ? null : (input.position ?? null),
      )
      if(isLatestCall()) {
        upsertItem(saved)
      }
      return saved
    } catch (error) {
      if(!existing && isLatestCall()) {
        removeItems(workspaceId, [input.resourceId])
      }
      messages.error(t('minimizedItems.messages.minimizeFailed'))
      return null
    }
  }

  async function removeMinimizedItem(resourceId: string, options: {withUndo?: boolean} = {}): Promise<void> {
    const workspaceId = currentWorkspaceId.value
    const item = getMinimizedItem(resourceId)
    if(!workspaceId || !item) {
      return
    }
    removeItems(workspaceId, [resourceId])
    try {
      await minimizedItemApiStore.deleteMinimizedItem(MINIMIZED_RESOURCE_TYPE, resourceId)
    } catch (error) {
      await fetchMinimizedItems(workspaceId)
      return
    }
    if(!options.withUndo || item.isUnavailable) {
      return
    }
    messages.undo(t('minimizedItems.messages.removed', {title: getMinimizedItemTitle(item)}), async() => {
      await minimizeRecord({
        resourceId: item.resourceId,
        databaseId: item.databaseId,
        viewState: item.viewState,
        preview: item.preview,
        position: item.position,
      })
    })
  }

  async function removeMinimizedItems(resourceIds: string[], options: {withUndo?: boolean} = {}): Promise<void> {
    const workspaceId = currentWorkspaceId.value
    if(!workspaceId || !resourceIds.length) {
      return
    }
    const removed = minimizedItems.value.filter(item => resourceIds.includes(item.resourceId))
    removeItems(workspaceId, resourceIds)
    try {
      await minimizedItemApiStore.deleteMinimizedItems(workspaceId, resourceIds)
    } catch (error) {
      await fetchMinimizedItems(workspaceId)
      return
    }
    const restorable = removed.filter(item => !item.isUnavailable)
    if(!options.withUndo || !restorable.length) {
      return
    }
    messages.undo(t('minimizedItems.messages.removedMany', {count: removed.length}), async() => {
      for(const item of sortByPosition(restorable)) {
        await minimizeRecord({
          resourceId: item.resourceId,
          databaseId: item.databaseId,
          viewState: item.viewState,
          preview: item.preview,
          position: item.position,
        })
      }
      // the server decided the final positions, so the bar shows what it answered even if one restore failed
      await fetchMinimizedItems(workspaceId)
    })
  }

  async function moveMinimizedItem(resourceId: string, targetIndex: number): Promise<void> {
    const workspaceId = currentWorkspaceId.value
    if(!workspaceId) {
      return
    }
    const items = getWorkspaceItems(workspaceId)
    const fromIndex = items.findIndex(item => item.resourceId === resourceId)
    const clampedIndex = Math.max(0, Math.min(items.length - 1, targetIndex))
    if(fromIndex === -1 || fromIndex === clampedIndex) {
      return
    }
    const next = items.slice()
    const [moved] = next.splice(fromIndex, 1)
    next.splice(clampedIndex, 0, moved)
    setWorkspaceItems(workspaceId, renumber(next))
    try {
      await minimizedItemApiStore.updateMinimizedItem(MINIMIZED_RESOURCE_TYPE, resourceId, {position: clampedIndex})
    } catch (error) {
      await fetchMinimizedItems(workspaceId)
    }
  }

  // Opening a record from the bar (or anywhere else) counts as seeing its changes, on every device
  async function markSeen(resourceId: string): Promise<void> {
    const item = getMinimizedItem(resourceId)
    if(!item || item.isUnavailable || item.id.startsWith('pending:')) {
      return
    }
    upsertItem({
      ...item,
      changeCount: 0,
      lastSeenAt: new Date(),
    })
    try {
      await minimizedItemApiStore.updateMinimizedItem(MINIMIZED_RESOURCE_TYPE, resourceId, {markSeen: true})
    } catch (error) {
      // the counter comes back with the next list or socket update
    }
  }

  function registerOpenRecord(openRecord: IOpenMinimizableRecord) {
    openRecords.value = [...openRecords.value.filter(existing => existing.key !== openRecord.key), openRecord]
  }

  function unregisterOpenRecord(key: symbol) {
    openRecords.value = openRecords.value.filter(existing => existing.key !== key)
  }

  function setPendingRestore(resourceId: string, viewState: IMinimizedItemViewState) {
    pendingRestoreByResourceId.set(resourceId, viewState)
  }

  function consumePendingRestore(resourceId: string): IMinimizedItemViewState | null {
    const viewState = pendingRestoreByResourceId.get(resourceId) ?? null
    pendingRestoreByResourceId.delete(resourceId)
    return viewState
  }

  function registerSlotElement(resourceId: string, element: HTMLElement | null) {
    if(element) {
      slotElementByResourceId.set(resourceId, element)
      return
    }
    slotElementByResourceId.delete(resourceId)
  }

  function registerOverflowElement(element: HTMLElement | null) {
    overflowElement = element
  }

  function registerBarElement(element: HTMLElement | null) {
    barElement = element
  }

  function getSlotElement(resourceId: string): HTMLElement | null {
    const element = slotElementByResourceId.get(resourceId)
    if(element?.isConnected) {
      return element
    }
    if(overflowElement?.isConnected) {
      return overflowElement
    }
    return barElement?.isConnected ? barElement : null
  }

  function requestFlash(resourceId: string) {
    flashRequest.value = {
      resourceId,
      at: Date.now(),
    }
  }

  function requestFlight(flight: Omit<IMinimizeFlightRequest, 'at'>) {
    flightRequest.value = {
      ...flight,
      at: Date.now(),
    }
  }

  function getMinimizedItemTitle(item: IMinimizedItemResponseInstance): string {
    return item.preview?.title?.trim() || t('general.noName')
  }

  const alwaysActive = {
    lifecycleFn() {
      return true
    },
  }

  function upsertFromSocket(workspaceId: string, minimizedItem: IToJson<IMinimizedItemResponseInstance>) {
    if(!hasLoaded(workspaceId)) {
      return
    }
    upsertItem(mapMinimizedItemResponseInstance(minimizedItem))
  }

  useSocketListener<IToJson<IMinimizedItemCreatedEmitPayload>>(GlobalSocketEventType.MinimizedItemCreated, payload => {
    if(!payload?.workspaceId || !payload.minimizedItem) {
      return
    }
    upsertFromSocket(payload.workspaceId, payload.minimizedItem)
  }, alwaysActive)

  useSocketListener<IToJson<IMinimizedItemUpdatedEmitPayload>>(GlobalSocketEventType.MinimizedItemUpdated, payload => {
    if(!payload?.workspaceId || !payload.minimizedItem) {
      return
    }
    upsertFromSocket(payload.workspaceId, payload.minimizedItem)
  }, alwaysActive)

  useSocketListener<IMinimizedItemDeletedEmitPayload>(GlobalSocketEventType.MinimizedItemDeleted, payload => {
    if(!payload?.workspaceId || !payload.resourceId) {
      return
    }
    removeItems(payload.workspaceId, [payload.resourceId])
  }, alwaysActive)

  useSocketListener<IMinimizedItemsReorderedEmitPayload>(GlobalSocketEventType.MinimizedItemsReordered, payload => {
    if(!payload?.workspaceId || !Array.isArray(payload.positions) || !hasLoaded(payload.workspaceId)) {
      return
    }
    const positionByResourceId = new Map(payload.positions.map(entry => [entry.resourceId, entry.position]))
    setWorkspaceItems(payload.workspaceId, getWorkspaceItems(payload.workspaceId).map(item => {
      const position = positionByResourceId.get(item.resourceId)
      return position === undefined || position === item.position ? item : {
        ...item,
        position,
      }
    }))
  }, alwaysActive)

  return {
    minimizedItems,
    minimizedResourceIds,
    openRecords,
    openResourceIds,
    topOpenRecord,
    flashRequest,
    flightRequest,
    hasLoaded,
    fetchMinimizedItems,
    getMinimizedItem,
    isMinimized,
    minimizeRecord,
    removeMinimizedItem,
    removeMinimizedItems,
    moveMinimizedItem,
    markSeen,
    registerOpenRecord,
    unregisterOpenRecord,
    setPendingRestore,
    consumePendingRestore,
    registerSlotElement,
    registerOverflowElement,
    registerBarElement,
    getSlotElement,
    requestFlash,
    requestFlight,
    getMinimizedItemTitle,
  }
})
